import { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';

let _configPromise = null;
export const getSecurityConfig = () => {
  if (!_configPromise) _configPromise = api.get('/security/config').then(({ data }) => data).catch(() => ({ turnstile_enabled: false, turnstile_site_key: '' }));
  return _configPromise;
};

const loadScript = () => new Promise((resolve) => {
  if (window.turnstile) return resolve();
  if (document.getElementById('cf-turnstile-script')) { const t = setInterval(() => { if (window.turnstile) { clearInterval(t); resolve(); } }, 100); return; }
  const s = document.createElement('script');
  s.id = 'cf-turnstile-script';
  s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  s.async = true; s.defer = true; s.onload = () => resolve();
  document.head.appendChild(s);
});

// Renders the Cloudflare Turnstile widget only when the platform has configured keys.
// When not configured, renders nothing and the app relies on built-in protections.
export const Turnstile = ({ onToken, className }) => {
  const ref = useRef(null);
  const widgetId = useRef(null);
  const [siteKey, setSiteKey] = useState('');

  useEffect(() => {
    let active = true;
    getSecurityConfig().then((cfg) => { if (active && cfg.turnstile_enabled && cfg.turnstile_site_key) setSiteKey(cfg.turnstile_site_key); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !window.turnstile || !ref.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token) => onToken?.(token),
        'expired-callback': () => onToken?.(''),
        'error-callback': () => onToken?.(''),
      });
    });
    return () => { cancelled = true; try { if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current); } catch { /* noop */ } widgetId.current = null; };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return <div ref={ref} className={className} data-testid="turnstile-widget" />;
};

// Hidden honeypot field: real users leave it empty; bots that auto-fill are rejected server-side.
export const Honeypot = ({ value, onChange }) => (
  <input type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true"
    value={value} onChange={(e) => onChange(e.target.value)} data-testid="honeypot-field"
    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
);

export const useHoneypot = () => {
  const [hp, setHp] = useState('');
  return { hp, setHp, field: <Honeypot value={hp} onChange={setHp} /> };
};

export const useTurnstileToken = () => {
  const [token, setToken] = useState('');
  const onToken = useCallback((t) => setToken(t), []);
  return { token, onToken, widget: <Turnstile onToken={onToken} className="mt-1" /> };
};
